<template>
  <div>
    <!-- Toolbar Aba C -->
    <b-row class="mb-1">
      <b-col cols="12" md="4" xl="2" class="mb-1">
        <b-form-input
          v-model="filters.spn"
          :placeholder="$t('Filtro SPN')"
          type="number"
          @input="debounceSearch"
        />
      </b-col>
      <b-col cols="12" md="4" xl="2" class="mb-1">
        <b-form-input
          v-model="filters.fmi"
          :placeholder="$t('Filtro FMI')"
          type="number"
          @input="debounceSearch"
        />
      </b-col>
      <b-col cols="12" md="4" xl="3" class="mb-1">
        <b-form-input
          v-model="filters.description"
          :placeholder="$t('Buscar descrição...')"
          @input="debounceSearch"
        />
      </b-col>
      
      <b-col cols="12" xl="5" class="d-flex align-items-center justify-content-xl-end flex-wrap mb-1">
        <b-button
          v-if="selectedItems.length > 0"
          variant="danger"
          class="mr-1 mb-1"
          @click="confirmBatchDelete"
        >
          <feather-icon icon="Trash2Icon" class="mr-50" />
          <span class="text-nowrap">{{ $t('Excluir') }} ({{ selectedItems.length }})</span>
        </b-button>
        <b-button variant="outline-info" class="mr-1 mb-1" @click="downloadTemplate" :disabled="isDownloading">
          <b-spinner v-if="isDownloading" small class="mr-50" />
          <feather-icon v-else icon="DownloadCloudIcon" class="mr-50" />
          <span class="text-nowrap">{{ $t('Baixar Modelo') }}</span>
        </b-button>
        <b-button variant="outline-primary" class="mr-1 mb-1" @click="$refs.importModal.show()">
          <feather-icon icon="UploadCloudIcon" class="mr-50" />
          <span class="text-nowrap">{{ $t('Importar') }}</span>
        </b-button>
        <b-button variant="primary" class="mb-1" @click="openAddModal">
          <feather-icon icon="PlusIcon" class="mr-50" />
          <span class="text-nowrap">{{ $t('Adicionar') }}</span>
        </b-button>
      </b-col>
    </b-row>

    <!-- Grid Virtualizada / Server-side -->
    <b-table
      :items="items"
      :fields="columns"
      responsive
      show-empty
      :empty-text="$t('Nenhum código DTC encontrado.')"
      selectable
      select-mode="multi"
      @row-selected="onRowSelected"
      :busy="isLoading"
      hover
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <!-- Loading skeleton effect -->
          <b-spinner class="align-middle"></b-spinner>
          <strong class="ml-1">{{ $t('Carregando códigos...') }}</strong>
        </div>
      </template>

      <!-- Checkbox column -->
      <template #cell(selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <feather-icon icon="CheckSquareIcon" size="18" class="text-primary" />
        </template>
        <template v-else>
          <feather-icon icon="SquareIcon" size="18" class="text-secondary" />
        </template>
      </template>

      <!-- Severidade (Chip) -->
      <template #cell(dtcType)="data">
        <b-badge :variant="resolveSeverityVariant(data.value)">
          {{ resolveSeverityText(data.value) }}
        </b-badge>
      </template>

      <!-- Ações -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <b-button variant="flat-primary" class="btn-icon mr-50" @click="openEditModal(data.item)">
            <feather-icon icon="EditIcon" />
          </b-button>
          <b-button variant="flat-danger" class="btn-icon" @click="confirmDelete(data.item.id)">
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </div>
      </template>
    </b-table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between mt-1">
      <span class="text-muted">{{ $t('Mostrando registros') }} {{ totalItems }}</span>
      <b-pagination
        v-model="currentPage"
        :total-rows="totalItems"
        :per-page="perPage"
        first-number
        last-number
      />
    </div>

    <!-- Modals (Add/Edit e Import) -->
    <dtc-item-modal
      ref="itemModal"
      :dtc-table-id="dtcTableId"
      @saved="handleItemSaved"
    />
    
    <dtc-import-modal
      ref="importModal"
      :dtc-table-id="dtcTableId"
      @imported="handleImportSuccess"
    />
  </div>
</template>

<script>
import { BRow, BCol, BFormInput, BButton, BTable, BPagination, BSpinner, BBadge } from 'bootstrap-vue'
import store from '@/store'
import DtcItemModal from './DtcItemModal.vue'
import DtcImportModal from './DtcImportModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'

export default {
  components: {
    BRow, BCol, BFormInput, BButton, BTable, BPagination, BSpinner, BBadge,
    DtcItemModal, DtcImportModal
  },
  props: {
    dtcTableId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      filters: {
        spn: '',
        fmi: '',
        description: ''
      },
      items: [],
      selectedItems: [],
      isLoading: false,
      currentPage: 1,
      perPage: 50, // Escala de milhares, carregamos em lotes de 50
      totalItems: 0,
      searchTimeout: null,
      isDownloading: false,
      columns: [
        { key: 'selected', label: '' },
        { key: 'spn', label: 'SPN' },
        { key: 'fmi', label: 'FMI' },
        { key: 'dtc', label: 'DTC' },
        { key: 'description', label: this.$t('Descrição') },
        { key: 'dtcType', label: this.$t('Severidade') },
        { key: 'actions', label: this.$t('Ações') }
      ]
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.fetchItems()
      }, 500)
    },
    fetchItems() {
      this.isLoading = true
      store.dispatch('app-dtc/fetchItems', {
        dtcTableId: this.dtcTableId,
        spn: this.filters.spn,
        fmi: this.filters.fmi,
        description: this.filters.description,
        page: this.currentPage,
        perPage: this.perPage
      }).then(response => {
        this.items = response.data?.data || []
        this.totalItems = response.data?.totalItems || 0
        this.$emit('update-count', this.totalItems)
      }).catch(() => {
        this.$toast({
          component: ToastificationContent,
          props: { title: this.$t('Erro ao buscar códigos'), icon: 'AlertTriangleIcon', variant: 'danger' }
        })
      }).finally(() => {
        this.isLoading = false
      })
    },
    onRowSelected(items) {
      this.selectedItems = items
    },
    openAddModal() {
      this.$refs.itemModal.showModal()
    },
    openEditModal(item) {
      this.$refs.itemModal.showModal(item)
    },
    handleItemSaved() {
      this.fetchItems()
    },
    handleImportSuccess() {
      this.currentPage = 1
      this.fetchItems()
    },
    confirmDelete(id) {
      this.executeDelete([id])
    },
    confirmBatchDelete() {
      const ids = this.selectedItems.map(i => i.id)
      this.executeDelete(ids)
    },
    executeDelete(ids) {
      this.$swal({
        title: this.$t('Tem certeza?'),
        text: this.$t('Deseja excluir os itens selecionados?'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Sim, excluir'),
        cancelButtonText: this.$t('Cancelar'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          store.dispatch('app-dtc/deleteItems', { dtcTableId: this.dtcTableId, ids })
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: { title: this.$t('Itens excluídos com sucesso'), icon: 'CheckIcon', variant: 'success' }
              })
              this.fetchItems()
              this.selectedItems = []
            })
            .catch(() => {
              this.$toast({
                component: ToastificationContent,
                props: { title: this.$t('Erro ao excluir itens'), icon: 'AlertTriangleIcon', variant: 'danger' }
              })
            })
        }
      })
    },
    resolveSeverityVariant(type) {
      // 0 UnMapped (cinza), 1 Notification (azul), 2 Warning (amarelo), 3 Danger (vermelho)
      switch (type) {
        case 1: return 'primary'
        case 2: return 'warning'
        case 3: return 'danger'
        case 0:
        default: return 'secondary'
      }
    },
    resolveSeverityText(type) {
      switch (type) {
        case 1: return this.$t('Notificação')
        case 2: return this.$t('Aviso')
        case 3: return this.$t('Perigo')
        case 0:
        default: return this.$t('Não Mapeado')
      }
    },
    downloadTemplate() {
      this.$swal({
        title: this.$t('Atenção ao Cabeçalho'),
        text: this.$t('Você não pode editar nem retirar o cabeçalho da planilha modelo, pois isso impedirá o sistema de reconhecer as colunas corretamente durante a importação.'),
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('Entendi, Baixar Planilha'),
        cancelButtonText: this.$t('Cancelar'),
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-danger ml-1' },
      }).then(result => {
        if (result.value) {
          this.executeDownload()
        }
      })
    },
    executeDownload() {
      this.isDownloading = true
      axios.get('/api/v1/DTC/table/items/import/model', { responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'Modelo_Importacao_DTC.xlsx')
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Download iniciado'), icon: 'CheckIcon', variant: 'success' }
          })
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Erro ao baixar planilha modelo'), icon: 'AlertTriangleIcon', variant: 'danger' }
          })
        })
        .finally(() => {
          this.isDownloading = false
        })
    }
  }
}
</script>

<style scoped>
.btn-icon {
  padding: 0.5rem;
}
</style>
